/**
 * Het profielblok onderaan de zijbalk (ontwerp v4 §2).
 *
 * Initialen, naam, rol; menu met Mijn profiel, Gebruikers en toegang,
 * Oud portaal, Uitloggen. De rollabels zijn die van v4 (meekijken / beantwoorden /
 * beheer, antwoord 7 van JP) terwijl de databasenamen ongewijzigd
 * blijven — vertalen doet :func:`rolLabel`, hernoemen doet niemand.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuthStore } from "@/lib/auth-store";
import { useLogout } from "@/lib/hooks/useLogout";

/** De rollabels uit v4 plus Toets, boven op de bestaande databaserollen. */
export function rolLabel(rol: string | undefined): string {
  switch (rol) {
    case "admin":
      return "Beheer";
    case "medewerker":
      return "Beantwoorden";
    case "alleen_lezen":
      return "Meekijken";
    case "toets":
      return "Toets";
    default:
      return "Onbekend";
  }
}

function initialen(naam: string | undefined): string {
  if (!naam) return "?";
  const delen = naam.trim().split(/\s+/).filter(Boolean);
  if (delen.length === 0) return "?";
  if (delen.length === 1) return delen[0].slice(0, 2).toUpperCase();
  return (delen[0][0] + delen[delen.length - 1][0]).toUpperCase();
}

export function WerkplekProfiel() {
  const gebruiker = useAuthStore((s) => s.gebruiker);
  const navigate = useNavigate();
  const { logout, bezig } = useLogout();
  const [open, setOpen] = useState(false);
  const blok = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function buiten(gebeurtenis: MouseEvent) {
      if (!blok.current?.contains(gebeurtenis.target as Node)) setOpen(false);
    }
    function ontsnap(gebeurtenis: KeyboardEvent) {
      if (gebeurtenis.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", buiten);
    document.addEventListener("keydown", ontsnap);
    return () => {
      document.removeEventListener("mousedown", buiten);
      document.removeEventListener("keydown", ontsnap);
    };
  }, [open]);

  return (
    <div
      ref={blok}
      style={{ marginTop: "auto", position: "relative" }}
      className="wp-profiel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: 8,
          border: 0,
          background: "transparent",
          color: "var(--wp-zij-tekst)",
          borderRadius: 8,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span
          aria-hidden
          style={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: "var(--wp-blauw)",
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontSize: 12,
            fontWeight: 600,
            flex: "none",
          }}
        >
          {initialen(gebruiker?.naam)}
        </span>
        <span className="wp-txt">
          <span
            className="wp-profiel-naam"
            style={{
              display: "block",
              color: "#fff",
              fontWeight: 600,
              fontSize: 13,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            {gebruiker?.naam ?? "Niet ingelogd"}
          </span>
          <span
            className="wp-profiel-rol"
            style={{
              display: "block",
              fontSize: 11,
              color: "var(--wp-tekst3)",
              whiteSpace: "nowrap",
            }}
          >
            {rolLabel(gebruiker?.rol)}
          </span>
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: "calc(100% + 6px)",
            background: "var(--wp-paneel)",
            color: "var(--wp-tekst)",
            border: "1px solid var(--wp-lijn)",
            borderRadius: 10,
            boxShadow: "0 8px 30px rgba(0,0,0,.25)",
            padding: 6,
            zIndex: 60,
            minWidth: 200,
          }}
        >
          <button
            type="button"
            role="menuitem"
            className="wp-menu-keuze"
            onClick={() => navigate("/profiel")}
            style={keuzeStijl}
          >
            Mijn profiel
          </button>
          <button
            type="button"
            role="menuitem"
            className="wp-menu-keuze"
            onClick={() => navigate("/beheer/gebruikers")}
            style={keuzeStijl}
          >
            Gebruikers en toegang
          </button>
          <button
            type="button"
            role="menuitem"
            className="wp-menu-keuze"
            disabled={bezig}
            onClick={() => void logout()}
            style={keuzeStijl}
          >
            Uitloggen
          </button>
        </div>
      ) : null}
    </div>
  );
}

const keuzeStijl: React.CSSProperties = {
  display: "flex",
  width: "100%",
  padding: "8px 10px",
  borderRadius: 7,
  border: 0,
  background: "transparent",
  color: "inherit",
  cursor: "pointer",
  fontSize: 13,
  textAlign: "left",
};
